// ════════════════════════════════════════════════════════════
// /depenses — Sprint 3 (mobile-first)
// ════════════════════════════════════════════════════════════
// Hub des dépenses : liste filtrable + workflow modules + tabs +
// recherche + FAB nouvelle dépense (mobile) + lien fournisseurs.
//
// Tous les rôles voient la liste, computeActions par dépense
// gère qui peut faire quoi (cf. statusMachine.ts).
// Mobile-first : cards verticales empilées, FAB en bas à droite.
// ════════════════════════════════════════════════════════════
import { useMemo, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  AlertCircle,
  AlertTriangle,
  Search,
  Receipt as ReceiptIcon,
  Building2,
  Wallet,
  ClipboardCheck,
  ArrowRightLeft,
  Plus,
  type LucideIcon,
} from 'lucide-react';
import {
  useDepenses,
  useDepenseCounts,
  useDepenseAnomalyCountsByDepense,
} from '@/queries/depenses';
import { useAuth } from '@/hooks/useAuth';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { cn } from '@/lib/utils';
import {
  STATUS_LABELS,
  STATUS_COLORS,
  APPROVAL_LABELS,
  APPROVAL_COLORS,
  canCreateDepense,
  type Depense,
  type DepenseStatus,
} from '@ecr/core/depenses';
import { formatAmount } from '@ecr/core/utils';

export const Route = createFileRoute('/_authed/depenses/')({
  component: DepensesIndex,
});

// ─── Tabs de filtrage par status ──────────────────────────
const TABS = [
  {
    key: 'demandées',
    label: 'Demandées',
    statuses: ['demandée'] as DepenseStatus[],
  },
  {
    key: 'transferts',
    label: 'Transferts en cours',
    statuses: ['en_cours_transfert'] as DepenseStatus[],
  },
  {
    key: 'payees',
    label: 'Payées',
    statuses: ['payée'] as DepenseStatus[],
  },
  {
    key: 'comptabilisees',
    label: 'Comptabilisées',
    statuses: ['comptabilisée'] as DepenseStatus[],
  },
  {
    key: 'toutes',
    label: 'Toutes',
    statuses: undefined as DepenseStatus[] | undefined,
  },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function DepensesIndex() {
  const { profile } = useAuth();
  // Le comptable atterrit directement sur sa file de travail : les dépenses
  // payées en attente de comptabilisation Sage (demande Sidi 2026-08-20).
  const [activeTab, setActiveTab] = useState<TabKey>(() =>
    profile?.role === 'comptable' ? 'payees' : 'demandées',
  );
  const [search, setSearch] = useState('');
  const currentTab = TABS.find((t) => t.key === activeTab) ?? TABS[0];
  // Demander un paiement = DAF only (Sidi/Abidine) — les autres consultent.
  const canCreate = canCreateDepense(profile?.role ?? null);

  const {
    data: depenses = [],
    isLoading,
    error,
  } = useDepenses({
    status: currentTab.statuses ? [...currentTab.statuses] : undefined,
    search,
  });

  const { data: counts } = useDepenseCounts();
  const { data: anomalyCounts = {} } = useDepenseAnomalyCountsByDepense();

  return (
    <div className="space-y-4">
      {/* Header compact mobile : title + boutons icônes, CTA en FAB */}
      <div className="space-y-2.5">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-foreground text-xl font-semibold tracking-tight sm:text-2xl">
            Achats &amp; Charges
          </h1>
          <div className="flex shrink-0 gap-1.5">
            <Link to="/depenses/fournisseurs" aria-label="Fournisseurs">
              <Button variant="outline" size="sm" className="gap-1.5">
                <Building2 className="size-4" />
                <span className="hidden sm:inline">Fournisseurs</span>
              </Button>
            </Link>
            {canCreate && (
              <Link to="/depenses/new" className="hidden sm:block">
                <Button className="gap-2">
                  <Plus className="size-4" />
                  Nouvelle dépense
                </Button>
              </Link>
            )}
          </div>
        </div>
        <p className="text-muted-foreground hidden text-sm sm:block">
          Factures fournisseurs, paiements &amp; comptabilisation
        </p>
      </div>

      {/* Workflow modules — vue d'ensemble en cards (2 cols mobile, 4 cols desktop) */}
      <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
        <WorkflowModule
          icon={ReceiptIcon}
          label="Demandées"
          count={counts?.demandée ?? 0}
          onClick={() => {
            setActiveTab('demandées');
          }}
        />
        <WorkflowModule
          icon={ArrowRightLeft}
          label="Transferts en cours"
          count={counts?.en_cours_transfert ?? 0}
          alertSeuil={5}
          onClick={() => {
            setActiveTab('transferts');
          }}
        />
        <WorkflowModule
          icon={Wallet}
          label="Payées (à compta)"
          count={counts?.payée ?? 0}
          alertSeuil={15}
          onClick={() => {
            setActiveTab('payees');
          }}
        />
        <WorkflowModule
          icon={ClipboardCheck}
          label="Comptabilisées"
          count={counts?.comptabilisée ?? 0}
          onClick={() => {
            setActiveTab('comptabilisees');
          }}
        />
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
        <Input
          type="search"
          placeholder="Rechercher bénéficiaire, fournisseur, n° facture, code…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tabs sticky — accessible pendant le scroll */}
      <div className="bg-background border-border sticky top-12 z-20 -mx-4 overflow-x-auto border-b sm:top-14 sm:mx-0">
        <div className="flex min-w-max gap-1 px-4 sm:min-w-0 sm:px-0">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors',
                activeTab === tab.key
                  ? 'border-primary text-foreground'
                  : 'text-muted-foreground hover:text-foreground border-transparent',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Contenu */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <DepenseCardSkeleton key={i} />
          ))}
        </div>
      ) : error ? (
        <Card>
          <CardContent className="flex items-center gap-3 py-6">
            <AlertCircle className="size-5 text-red-600" />
            <p className="text-sm">
              Erreur de chargement : {error instanceof Error ? error.message : 'inconnu'}
            </p>
          </CardContent>
        </Card>
      ) : depenses.length === 0 ? (
        <EmptyState tab={currentTab.label} hasSearch={search.length > 0} canCreate={canCreate} />
      ) : (
        <DepenseList depenses={depenses} anomalyCounts={anomalyCounts} />
      )}

      {/* FAB mobile-first : nouvelle dépense (cachée sur desktop) */}
      {canCreate && (
        <Link
          to="/depenses/new"
          className="bg-primary text-primary-foreground fixed bottom-[calc(4rem+env(safe-area-inset-bottom,0px)+0.75rem)] right-6 z-40 flex size-14 items-center justify-center rounded-full shadow-lg transition-transform hover:scale-105 active:scale-95 sm:hidden"
          aria-label="Nouvelle dépense"
        >
          <Plus className="size-6" />
        </Link>
      )}
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────
function EmptyState({
  tab,
  hasSearch,
  canCreate,
}: {
  tab: string;
  hasSearch: boolean;
  canCreate: boolean;
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-3 py-12 text-center">
        <ReceiptIcon className="text-muted-foreground size-12" />
        <div>
          <p className="text-foreground font-medium">
            {hasSearch ? 'Aucun résultat' : 'Aucune dépense'}
          </p>
          <p className="text-muted-foreground text-sm">
            {hasSearch
              ? 'Aucune dépense ne correspond à ta recherche.'
              : `Aucune dépense dans la catégorie « ${tab.toLowerCase()} ».`}
          </p>
        </div>
        {!hasSearch && canCreate && (
          <Link to="/depenses/new">
            <Button variant="outline" className="mt-2 gap-2">
              <Plus className="size-4" />
              Créer une dépense
            </Button>
          </Link>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Workflow module compteur ─────────────────────────────
interface WorkflowModuleProps {
  icon: LucideIcon;
  label: string;
  count: number;
  alertSeuil?: number;
  onClick: () => void;
}

function WorkflowModule({ icon: Icon, label, count, alertSeuil, onClick }: WorkflowModuleProps) {
  const isAlert = alertSeuil != null && count >= alertSeuil;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'border-border bg-card flex items-center gap-3 rounded-lg border p-3 text-left transition-all',
        'hover:bg-accent hover:border-accent-foreground/20 active:scale-[0.98]',
        isAlert && 'border-red-300 dark:border-red-900',
      )}
    >
      <div
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-md sm:size-10',
          isAlert
            ? 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300'
            : 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="size-4 sm:size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            'text-xl font-semibold leading-none sm:text-2xl',
            isAlert ? 'text-red-700 dark:text-red-300' : 'text-foreground',
          )}
        >
          {count}
        </p>
        <p className="text-muted-foreground mt-0.5 truncate text-[11px] sm:text-xs">{label}</p>
      </div>
    </button>
  );
}

// ─── Liste cards ──────────────────────────────────────────
function DepenseList({
  depenses,
  anomalyCounts,
}: {
  depenses: Depense[];
  anomalyCounts: Record<string, number>;
}) {
  return (
    <div className="grid gap-2.5">
      {depenses.map((d) => (
        <DepenseCard key={d.id} depense={d} anomalyCount={anomalyCounts[d.id] ?? 0} />
      ))}
    </div>
  );
}

function DepenseCard({ depense, anomalyCount }: { depense: Depense; anomalyCount: number }) {
  const statusColors = STATUS_COLORS[depense.status];
  const approvalColors = depense.approvalStatus ? APPROVAL_COLORS[depense.approvalStatus] : null;
  const dateStr = useMemo(
    () =>
      new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: 'short' }).format(
        depense.createdAt,
      ),
    [depense.createdAt],
  );

  return (
    <Link
      to="/depenses/$id"
      params={{ id: depense.id }}
      className="block transition-all hover:opacity-90 active:scale-[0.99]"
    >
      <Card>
        <CardContent className="flex flex-col gap-2 py-3">
          {/* Ligne 1 : code + badges */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-foreground font-mono text-sm font-semibold">{depense.code}</span>
            <span
              className="rounded-full px-2 py-0.5 text-xs font-medium"
              style={{ color: statusColors.fg, backgroundColor: statusColors.bg }}
            >
              {STATUS_LABELS[depense.status]}
            </span>
            {approvalColors && depense.approvalStatus && (
              <span
                className="rounded-full px-2 py-0.5 text-xs font-medium"
                style={{ color: approvalColors.fg, backgroundColor: approvalColors.bg }}
              >
                {APPROVAL_LABELS[depense.approvalStatus]}
              </span>
            )}
            {anomalyCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-0.5 text-xs font-medium text-white">
                <AlertTriangle className="size-3" />
                {anomalyCount}
              </span>
            )}
          </div>

          {/* Ligne 2 : montant + bénéficiaire */}
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-foreground truncate text-sm font-medium">
              {depense.fournisseurNom ?? depense.beneficiaire}
            </p>
            <p className="text-foreground shrink-0 font-mono text-base font-semibold tabular-nums">
              {formatAmount(
                depense.montant,
                depense.devise === 'EUR' || depense.devise === 'USD' ? depense.devise : 'MRU',
              )}
            </p>
          </div>

          {/* Ligne 3 : meta (date + num_facture + mode) */}
          <div className="text-muted-foreground flex items-center gap-2 text-xs">
            <span>{dateStr}</span>
            <span>·</span>
            <span className="capitalize">{depense.modePaiementSouhaite}</span>
            {depense.numFacture && (
              <>
                <span>·</span>
                <span>Facture {depense.numFacture}</span>
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function DepenseCardSkeleton() {
  return (
    <Card>
      <CardContent className="space-y-2 py-3">
        <div className="flex gap-2">
          <div className="bg-muted h-5 w-20 animate-pulse rounded" />
          <div className="bg-muted h-5 w-16 animate-pulse rounded-full" />
        </div>
        <div className="flex justify-between">
          <div className="bg-muted h-5 w-1/2 animate-pulse rounded" />
          <div className="bg-muted h-5 w-24 animate-pulse rounded" />
        </div>
        <div className="bg-muted h-3 w-2/3 animate-pulse rounded" />
      </CardContent>
    </Card>
  );
}
